/**
 * Dashboard Page
 *
 * CRM-style project management with Deal Pipeline and Portfolio Companies
 * Premium design following Vercel UI patterns
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  FolderKanban,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Briefcase,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowUpDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProjects, type DealStructureType } from '../hooks/useProjects';
import { usePortfolioCompanies, type PortfolioCompany } from '../hooks/usePortfolioCompanies';
import { useCreatorProfiles } from '../hooks/useCreatorProfiles';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { CreateProjectDialog } from '../components/projects/CreateProjectDialog';
import { ProjectsTable } from '../components/projects/ProjectsTable';
import { EmptyState } from '../components/ui/empty-state';
import {
  getProjectRoute,
  setLastUsedProject,
} from '../lib/navigation';
import { CreatorBadge } from '../components/shared/CreatorBadge';
import { CIMTeaserDashboardCard } from '../features/cim-teaser/CIMTeaserDashboardCard';
import { countDealsByStage } from '../lib/dealFunnel';
import { cn } from '../lib/utils';

// ============================================================================
// Portfolio Companies Table Types & Helpers
// ============================================================================

type PortfolioSortKey = 'name' | 'industry' | 'revenue' | 'revenueGrowth' | 'ebitda' | 'ebitdaMargin' | 'debtToEbitda';
type SortDirection = 'asc' | 'desc';

interface PortfolioSortConfig {
  key: PortfolioSortKey;
  direction: SortDirection;
}

// Format currency in compact notation
const formatCompactCurrency = (value: number): string => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

// Format currency in millions for table
const formatMillions = (value: number | undefined): React.ReactNode => {
  if (!value) return <span className="text-muted-foreground">-</span>;
  return `$${(value / 1000000).toFixed(1)}M`;
};

// Format percentage (0 reads as "no data" — companies without financials
// roll up a 0 margin, which would otherwise render a misleading "0.0%")
const formatPct = (value: number | undefined): React.ReactNode => {
  if (!value) return <span className="text-muted-foreground">-</span>;
  return `${value.toFixed(1)}%`;
};

// Format growth with color and icon
const formatGrowth = (value: number | undefined): React.ReactNode => {
  if (value === undefined || value === null || value === 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  const isPositive = value > 0;
  return (
    <span className={`flex items-center gap-0.5 font-medium ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
};

// Format leverage ratio with color coding
const formatLeverage = (value: number | undefined): React.ReactNode => {
  if (value === undefined || value === null) return <span className="text-muted-foreground">-</span>;
  const color = value > 5 ? 'text-red-600 dark:text-red-400' : value > 4 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground';
  return <span className={color}>{value.toFixed(1)}x</span>;
};

export const Dashboard: React.FC = () => {
  const { user, userProfile } = useAuth();
  const { projects, loading, error: projectsError, deleteProject, updateProject, pinnedProjects, togglePinProject } = useProjects();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Portfolio companies data
  const {
    activeCompanies: portfolioCompanies,
    metrics: portfolioMetrics,
    loading: portfolioLoading,
    error: portfolioError,
  } = usePortfolioCompanies();
  const portfolioCreatorProfiles = useCreatorProfiles(portfolioCompanies.map((company) => company.createdBy));

  // Portfolio table sort state
  const [portfolioSort, setPortfolioSort] = useState<PortfolioSortConfig>({
    key: 'revenue',
    direction: 'desc',
  });

  // Create project dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Handle portfolio sort
  const handlePortfolioSort = useCallback((key: PortfolioSortKey) => {
    setPortfolioSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // Live one-line digest of where the firm's pipeline stands
  const pipelineDigest = useMemo(() => {
    if (loading || projects.length === 0) return null;
    const counts = countDealsByStage(projects);
    const parts: string[] = [];
    if (counts.screening > 0) parts.push(`${counts.screening} in screening`);
    if (counts.diligence > 0) parts.push(`${counts.diligence} in diligence`);
    if (counts.ic > 0) parts.push(`${counts.ic} at IC`);
    if (!portfolioLoading && portfolioMetrics.companyCount > 0) {
      parts.push(
        `${portfolioMetrics.companyCount} portfolio ${portfolioMetrics.companyCount === 1 ? 'company' : 'companies'}`
      );
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [loading, projects, portfolioLoading, portfolioMetrics.companyCount]);

  const getPortfolioCreatorName = useCallback((company: PortfolioCompany): string => {
    if (!company.createdBy) return 'Unknown';
    const creator = portfolioCreatorProfiles.get(company.createdBy);
    return creator?.displayName || creator?.email || `${company.createdBy.slice(0, 8)}...`;
  }, [portfolioCreatorProfiles]);

  // Sorted portfolio companies (limited to 10 for dashboard view)
  const sortedPortfolioCompanies = useMemo(() => {
    const sorted = [...portfolioCompanies].sort((a, b) => {
      const { key, direction } = portfolioSort;
      const multiplier = direction === 'asc' ? 1 : -1;

      let aValue: string | number;
      let bValue: string | number;

      switch (key) {
        case 'name':
          aValue = (a.companyName || a.name || '').toLowerCase();
          bValue = (b.companyName || b.name || '').toLowerCase();
          break;
        case 'industry':
          aValue = (a.industry || '').toLowerCase();
          bValue = (b.industry || '').toLowerCase();
          break;
        case 'revenue':
          aValue = a.revenue || 0;
          bValue = b.revenue || 0;
          break;
        case 'revenueGrowth':
          aValue = a.revenueGrowth || 0;
          bValue = b.revenueGrowth || 0;
          break;
        case 'ebitda':
          aValue = a.ebitda || 0;
          bValue = b.ebitda || 0;
          break;
        case 'ebitdaMargin':
          aValue = a.ebitdaMargin || 0;
          bValue = b.ebitdaMargin || 0;
          break;
        case 'debtToEbitda':
          aValue = a.debtToEbitda || 0;
          bValue = b.debtToEbitda || 0;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * multiplier;
      }
      return ((aValue as number) - (bValue as number)) * multiplier;
    });

    return sorted.slice(0, 10); // Show top 10 on dashboard
  }, [portfolioCompanies, portfolioSort]);

  // Handle ?create=true query parameter from ProjectSwitcher
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setCreateDialogOpen(true);
      // Remove the query param to prevent re-triggering on navigation
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Handle project creation completion
   */
  const handleProjectCreated = (projectId: string) => {
    setLastUsedProject(projectId);
    navigate(getProjectRoute(projectId));
  };

  /**
   * Handle project deletion
   */
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  /**
   * Handle project edit (Deal Source, Sector, Transaction Type)
   */
  const handleEditProject = async (projectId: string, updates: {
    industry?: string;
    dealStructureType?: DealStructureType;
    metadata?: { dealSource?: string };
  }) => {
    try {
      await updateProject(projectId, updates);
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error; // Re-throw so the dialog can handle the error
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-10">
        {/* Welcome Section with Action */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Welcome back, {userProfile?.displayName?.split(' ')[0]}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {pipelineDigest ?? 'Your AI-powered due diligence workspace'}
            </p>
          </div>
          <div className="sm:flex-shrink-0">
            <CreateProjectDialog
              open={createDialogOpen}
              onOpenChange={setCreateDialogOpen}
              onProjectCreated={handleProjectCreated}
            />
          </div>
        </div>

        <div className="mb-8">
          <CIMTeaserDashboardCard />
        </div>

        {/* Projects Table - CRM Style */}
        <Card className="mb-6 border-border bg-card/80 backdrop-blur-sm shadow-md">
          <CardHeader className="border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-foreground">Deal Pipeline</CardTitle>
                <CardDescription className="text-muted-foreground">Your active due diligence projects</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {projectsError && projects.length === 0 ? (
              <EmptyState
                icon={AlertTriangle}
                title="Couldn't load your deals"
                description="Something went wrong loading your pipeline — your deals are safe. Please retry."
                action={{
                  label: 'Retry',
                  onClick: () => window.location.reload(),
                }}
              />
            ) : !loading && projects.length === 0 ? (
              <EmptyState
                icon={FolderKanban}
                title="No projects yet"
                description="Get started by creating your first due diligence project to track deals and analyze opportunities."
                illustration={{
                  light: '/images/empty-projects-light.jpg',
                  dark: '/images/empty-projects-dark.jpg',
                }}
                action={{
                  label: 'Create Project',
                  onClick: () => setCreateDialogOpen(true),
                }}
              />
            ) : (
              <ProjectsTable
                projects={projects}
                loading={loading}
                maxVisibleRows={10}
                showFunnel
                currentUserId={user?.uid}
                pinnedProjects={pinnedProjects}
                onDeleteProject={handleDeleteProject}
                onEditProject={handleEditProject}
                onTogglePin={togglePinProject}
              />
            )}
          </CardContent>
        </Card>

        {/* ================================================================== */}
        {/* Portfolio Companies Section - Premium Design */}
        {/* ================================================================== */}
        <Card className="mb-6 border-border bg-card/80 backdrop-blur-sm shadow-md overflow-hidden">
          <CardHeader className="border-b border-border bg-muted/30">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">Portfolio Companies</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {portfolioMetrics.companyCount} active investments
                  </CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/portfolio')}
                className="group"
              >
                View Full Portfolio
                <ArrowUpRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {/* Aggregate Metrics Bar */}
            {!portfolioLoading && portfolioMetrics.companyCount > 0 && (
              <div className="border-b border-border/50 bg-muted/20">
                <div className="grid grid-cols-2 gap-y-4 px-6 py-4 sm:grid-cols-4 sm:gap-y-0 sm:divide-x sm:divide-border">
                  {[
                    { label: 'Total Revenue', value: formatCompactCurrency(portfolioMetrics.totalRevenue) },
                    { label: 'Total EBITDA', value: formatCompactCurrency(portfolioMetrics.totalEbitda) },
                    { label: 'Avg Margin', value: `${portfolioMetrics.avgEbitdaMargin.toFixed(1)}%` },
                    { label: 'Companies', value: String(portfolioMetrics.companyCount) },
                  ].map((metric, index) => (
                    <div key={metric.label} className={index === 0 ? 'sm:pr-6' : 'sm:px-6'}>
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        {metric.label}
                      </p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                        {metric.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio Table */}
            {portfolioLoading ? (
              <div className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : portfolioError && portfolioCompanies.length === 0 ? (
              <EmptyState
                icon={AlertTriangle}
                title="Couldn't load your portfolio"
                description="Something went wrong loading your portfolio companies. Please retry."
                action={{
                  label: 'Retry',
                  onClick: () => window.location.reload(),
                }}
              />
            ) : portfolioCompanies.length === 0 ? (
              <div className="p-8 text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-muted/50 flex items-center justify-center mb-4">
                  <Briefcase className="h-7 w-7 text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-1">No portfolio companies yet</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  When deals close, transition them to "Portfolio Company" status to track performance here.
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                  View Deal Pipeline
                </Button>
              </div>
            ) : (
              <TooltipProvider>
                <div className="max-h-[600px] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <PortfolioSortableHeader
                          sortKey="name"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="min-w-[160px]"
                        >
                          Company
                        </PortfolioSortableHeader>
                        <PortfolioSortableHeader
                          sortKey="industry"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="min-w-[100px]"
                        >
                          Industry
                        </PortfolioSortableHeader>
                        <TableHead className="min-w-[140px]">Creator</TableHead>
                        <PortfolioSortableHeader
                          sortKey="revenue"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="text-right min-w-[90px]"
                        >
                          Revenue
                        </PortfolioSortableHeader>
                        <PortfolioSortableHeader
                          sortKey="revenueGrowth"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="text-right min-w-[80px]"
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>YoY %</span>
                            </TooltipTrigger>
                            <TooltipContent>Revenue Year-over-Year Growth</TooltipContent>
                          </Tooltip>
                        </PortfolioSortableHeader>
                        <PortfolioSortableHeader
                          sortKey="ebitda"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="text-right min-w-[90px]"
                        >
                          EBITDA
                        </PortfolioSortableHeader>
                        <PortfolioSortableHeader
                          sortKey="ebitdaMargin"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="text-right min-w-[70px]"
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>Margin</span>
                            </TooltipTrigger>
                            <TooltipContent>EBITDA Margin (EBITDA / Revenue)</TooltipContent>
                          </Tooltip>
                        </PortfolioSortableHeader>
                        <PortfolioSortableHeader
                          sortKey="debtToEbitda"
                          currentSort={portfolioSort}
                          onSort={handlePortfolioSort}
                          className="text-right min-w-[80px]"
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>Leverage</span>
                            </TooltipTrigger>
                            <TooltipContent>Net Debt / EBITDA</TooltipContent>
                          </Tooltip>
                        </PortfolioSortableHeader>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedPortfolioCompanies.map((company) => (
                        <TableRow
                          key={company.id}
                          className="transition-colors hover:bg-muted/40"
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-status-success-foreground" aria-hidden="true" />
                              <Link
                                to={`/portfolio/${company.id}`}
                                className="block max-w-[240px] truncate underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                title={company.companyName || company.name}
                              >
                                {company.companyName || company.name}
                              </Link>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs font-normal">
                              {company.industry || 'Other'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <CreatorBadge
                              creator={company.createdBy ? portfolioCreatorProfiles.get(company.createdBy) : null}
                              fallbackName={getPortfolioCreatorName(company)}
                              avatarClassName="h-6 w-6"
                              nameClassName="max-w-[110px]"
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">{formatMillions(company.revenue)}</TableCell>
                          <TableCell className="whitespace-nowrap text-right tabular-nums">{formatGrowth(company.revenueGrowth)}</TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">{formatMillions(company.ebitda)}</TableCell>
                          <TableCell className="whitespace-nowrap text-right tabular-nums">{formatPct(company.ebitdaMargin)}</TableCell>
                          <TableCell className="whitespace-nowrap text-right tabular-nums">{formatLeverage(company.debtToEbitda)}</TableCell>
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* View More Link */}
                {portfolioCompanies.length > 10 && (
                  <div className="px-6 py-3 border-t border-border/50 bg-muted/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground hover:text-foreground"
                      onClick={() => navigate('/portfolio')}
                    >
                      View all {portfolioCompanies.length} portfolio companies
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </TooltipProvider>
            )}
          </CardContent>
        </Card>

      </main>

    </div>
  );
};

// ============================================================================
// Portfolio Sortable Header Component
// ============================================================================

interface PortfolioSortableHeaderProps {
  sortKey: PortfolioSortKey;
  currentSort: PortfolioSortConfig;
  onSort: (key: PortfolioSortKey) => void;
  children: React.ReactNode;
  className?: string;
}

const PortfolioSortableHeader: React.FC<PortfolioSortableHeaderProps> = ({
  sortKey,
  currentSort,
  onSort,
  children,
  className = '',
}) => {
  const isActive = currentSort.key === sortKey;
  const ariaSort = isActive
    ? currentSort.direction === 'asc' ? 'ascending' : 'descending'
    : 'none';

  return (
    <TableHead
      className={className}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'flex h-full w-full items-center whitespace-nowrap py-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          className.includes('text-right') && 'justify-end'
        )}
      >
        {children}
        {isActive ? (
          currentSort.direction === 'asc' ? (
            <ChevronUp className="h-3 w-3 ml-1 text-primary" />
          ) : (
            <ChevronDown className="h-3 w-3 ml-1 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />
        )}
      </button>
    </TableHead>
  );
};
